import React from 'react';
import { DollarSign, TrendingUp, BarChart3, Activity, Shield, AlertTriangle, ArrowUpRight, ArrowDownRight, Globe } from 'lucide-react';
import { useOverview, useMarketPrices, useMarketOverview } from '@/hooks/useApi';
import { StatCard, Card, LoadingSpinner, Badge } from '@/components/UI';
import { SectionErrorBoundary } from '@/components/ErrorBoundary';
import { formatCurrency, formatPercent, pnlColor, pnlBgColor, timeAgo } from '@/utils';
import type { Trade, AccountSummary, PriceInfo, MarketOverview as MarketOverviewType } from '@/types';

const MARKET_WATCHLIST = 'BTC/USDT,ETH/USDT,SOL/USDT,XRP/USDT,ADA/USDT,DOGE/USDT,DOT/USDT,AVAX/USDT,LINK/USDT,MATIC/USDT';

export function OverviewPage() {
  const { data: overview, isLoading } = useOverview();
  const { data: marketPrices } = useMarketPrices(MARKET_WATCHLIST);
  const { data: marketOverview } = useMarketOverview();

  if (isLoading) return <LoadingSpinner text="Loading dashboard..." />;

  const totalValue = overview?.total_value ?? 0;
  const totalPnl = overview?.total_pnl ?? 0;
  const totalPnlPct = overview?.total_pnl_pct ?? 0;
  const activeStrategies = overview?.active_strategies ?? 0;
  const openPositions = overview?.open_positions ?? 0;
  const accounts = overview?.accounts ?? [];
  const recentTrades = overview?.recent_trades ?? [];
  const riskStatus = overview?.risk_status;
  const systemHealth = overview?.system_health;

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-surface-100">Dashboard Overview</h1>
        <div className="flex items-center gap-2">
          {riskStatus?.is_blocked && (
            <Badge variant="loss">
              <AlertTriangle className="w-3 h-3 mr-1" /> Risk Blocked
            </Badge>
          )}
          <Badge variant={systemHealth?.status === 'healthy' ? 'profit' : systemHealth?.status === 'degraded' ? 'warning' : 'loss'}>
            {systemHealth?.status?.toUpperCase() ?? 'Unknown'}
          </Badge>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Portfolio Value"
          value={formatCurrency(totalValue)}
          change={totalPnlPct}
          changeLabel="PnL"
          icon={<DollarSign className="w-5 h-5" />}
        />
        <StatCard
          label="Total P&L"
          value={`${totalPnl >= 0 ? '+' : ''}${formatCurrency(totalPnl)}`}
          icon={<TrendingUp className="w-5 h-5" />}
        />
        <StatCard
          label="Active Strategies"
          value={activeStrategies}
          icon={<BarChart3 className="w-5 h-5" />}
        />
        <StatCard
          label="Open Positions"
          value={openPositions}
          icon={<Activity className="w-5 h-5" />}
        />
      </div>

      {/* Market Overview Strip */}
      {marketOverview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="card p-3 flex items-center gap-2">
            <Globe className="w-4 h-4 text-brand-400" />
            <div>
              <p className="text-[10px] text-surface-500 uppercase">Market Cap</p>
              <p className="text-sm font-bold text-surface-200">
                {formatCurrency((marketOverview as MarketOverviewType).total_market_cap)}
              </p>
            </div>
          </div>
          <div className="card p-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-brand-400" />
            <div>
              <p className="text-[10px] text-surface-500 uppercase">24h Volume</p>
              <p className="text-sm font-bold text-surface-200">
                {formatCurrency((marketOverview as MarketOverviewType).total_volume_24h)}
              </p>
            </div>
          </div>
          <div className="card p-3 flex items-center gap-2">
            <span className="text-xs font-bold text-brand-400">₿</span>
            <div>
              <p className="text-[10px] text-surface-500 uppercase">BTC Dom</p>
              <p className="text-sm font-bold text-surface-200">
                {((marketOverview as MarketOverviewType).btc_dominance ?? 0).toFixed(1)}%
              </p>
            </div>
          </div>
          <div className="card p-3 flex items-center gap-2">
            <span className="text-xs font-bold text-brand-400">Ξ</span>
            <div>
              <p className="text-[10px] text-surface-500 uppercase">ETH Dom</p>
              <p className="text-sm font-bold text-surface-200">
                {((marketOverview as MarketOverviewType).eth_dominance ?? 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Live Market Prices */}
      <SectionErrorBoundary name="Market Prices">
      {marketPrices && (marketPrices as PriceInfo[]).length > 0 && (
        <Card title="Live Market Prices" subtitle="Auto-refreshing every 15s from CoinGecko">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {(marketPrices as PriceInfo[]).map((coin: PriceInfo) => {
              const isPositive = coin.change_24h_pct >= 0;
              const baseSymbol = coin.symbol.split('/')[0];
              return (
                <div
                  key={coin.symbol}
                  className="p-3 rounded-lg bg-surface-800/40 hover:bg-surface-800/70 transition-colors border border-surface-700/30"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-surface-300">{baseSymbol}</span>
                    <div className={`flex items-center gap-0.5 text-[10px] font-medium ${pnlColor(coin.change_24h_pct)}`}>
                      {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {formatPercent(coin.change_24h_pct)}
                    </div>
                  </div>
                  <p className="text-sm font-bold text-surface-100">{formatCurrency(coin.price)}</p>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      </SectionErrorBoundary>

      {/* Accounts & Recent Trades */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Accounts */}
        <SectionErrorBoundary name="Accounts">
        <Card title="Strategy Accounts" subtitle={`${accounts.length} accounts`}>
          <div className="space-y-3">
            {accounts.map((account: AccountSummary) => (
              <div
                key={account.id}
                className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${account.is_active ? 'bg-profit' : 'bg-surface-600'}`} />
                  <div>
                    <p className="text-sm font-medium text-surface-200">{account.name}</p>
                    <p className="text-xs text-surface-500">
                      {account.mode.toUpperCase()} • {account.positions_count} positions
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-surface-200">{formatCurrency(account.balance)}</p>
                  <p className={`text-xs font-medium ${pnlColor(account.pnl_pct)}`}>
                    {formatPercent(account.pnl_pct)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
        </SectionErrorBoundary>

        {/* Recent Trades */}
        <SectionErrorBoundary name="Recent Trades">
        <Card title="Recent Trades" action={<span className="text-xs text-surface-500">Last 10</span>}>
          <div className="space-y-2">
            {recentTrades.length === 0 ? (
              <p className="text-sm text-surface-500 text-center py-4">No recent trades</p>
            ) : (
              recentTrades.slice(0, 10).map((trade: Trade) => (
                <div
                  key={trade.id}
                  className="flex items-center justify-between py-2 border-b border-surface-800/50 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={trade.side === 'buy' ? 'profit' : 'loss'}>
                      {trade.side.toUpperCase()}
                    </Badge>
                    <div>
                      <p className="text-sm text-surface-200">{trade.symbol}</p>
                      <p className="text-xs text-surface-500">{trade.quantity} @ {formatCurrency(trade.price)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-medium ${pnlColor(trade.pnl ?? 0)}`}>
                      {trade.pnl !== null ? formatCurrency(trade.pnl) : '—'}
                    </p>
                    <p className="text-[10px] text-surface-600">{timeAgo(trade.created_at)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
        </SectionErrorBoundary>
      </div>

      {/* Risk Status */}
      {riskStatus && (
        <Card title="Risk Status">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-surface-500">Daily P&L</p>
              <p className={`text-sm font-medium ${pnlColor(riskStatus.daily_pnl)}`}>
                {formatCurrency(riskStatus.daily_pnl)}
              </p>
            </div>
            <div>
              <p className="text-xs text-surface-500">Weekly P&L</p>
              <p className={`text-sm font-medium ${pnlColor(riskStatus.weekly_pnl)}`}>
                {formatCurrency(riskStatus.weekly_pnl)}
              </p>
            </div>
            <div>
              <p className="text-xs text-surface-500">Current Drawdown</p>
              <p className="text-sm font-medium text-loss">
                {formatPercent(riskStatus.current_drawdown)}
              </p>
            </div>
            <div>
              <p className="text-xs text-surface-500">Emergency Shutdown</p>
              <p className={`text-sm font-medium ${riskStatus.emergency_shutdown?.is_active ? 'text-loss' : 'text-profit'}`}>
                {riskStatus.emergency_shutdown?.is_active ? 'ACTIVE' : 'Inactive'}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
